import React, { useEffect, useState, useContext } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

import { DataGrid, GridToolbar } from '@mui/x-data-grid';

import SearchIcon from '@mui/icons-material/Search';

import { AdminAxios as Axios } from '../../Assets/Axios/Axios';

import IOSSwitch from '../../Components/IOSSwitch';

import { COLORS } from '../../Constant/COLORS';

import ToastContext from '../../Context/ToastContext';

import { toCapitalize } from '../../Helpers';

import { ServicesQuery } from '../../Server/Query';
import { UpdateServiceIsPopular } from '../../Server/Mutation';

import ServiceModal from './Components/ServiceModal';
import ConfirmationModal from './Components/ConfirmationModal';

function Services() {
  const { showToastMessage } = useContext(ToastContext);

  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingServiceUpdateData, setPendingServiceUpdateData] = useState({
    id: null,
    isPopular: null,
  });

  useEffect(() => {
    setIsLoading(true);
    Axios(ServicesQuery)
      .then(({ data, errors }) => {
        if (errors) {
          showToastMessage("For some reason, we couldn't load services");
          return undefined;
        }

        const services = (data?.Services ?? []).map(parseService);
        setServices(services);
      })
      .catch(() => showToastMessage("For some reason, we couldn't load services"))
      .finally(() => setIsLoading(false));
  }, [showToastMessage]);

  async function togglePopularService() {
    const { id, isPopular } = pendingServiceUpdateData;

    const query = UpdateServiceIsPopular({ serviceId: id, isPopular });
    const { errors } = await Axios(query);
    if (errors) {
      closeConfirmationModal();
      showToastMessage("For some reason, we couldn't update");
      return undefined;
    }

    const tempServices = JSON.parse(JSON.stringify(services));
    tempServices.forEach(service => {
      if (service.id === id) service.isPopularService = isPopular;
    });
    setServices(tempServices);
    closeConfirmationModal();
  }

  const handleChangePopularService = React.useCallback((event, serviceId) => {
    setPendingServiceUpdateData({
      isPopular: event.target.checked,
      id: serviceId,
    });
    setIsConfirmModalOpen(true);
  }, []);

  function handleServiceModalClose() {
    setSelectedServiceId(null);
  }

  const tableColumns = React.useMemo(
    () => [
      {
        field: 'professionalId',
        headerName: 'Prof.ID',
        minWidth: 100,
        flex: 2,
      },
      {
        field: 'profName',
        headerName: 'Prof.fullName',
        minWidth: 150,
        flex: 3,
      },
      {
        field: 'serviceID',
        headerName: 'Service ID',
        minWidth: 100,
        flex: 2,
      },
      {
        field: 'name',
        headerName: 'Service Name',
        minWidth: 150,
        flex: 3,
      },
      {
        field: 'isPopularService',
        headerName: 'Popular Service',
        minWidth: 150,
        flex: 3,
        renderCell: params => (
          <IOSSwitch
            checked={params.value}
            onChange={event => handleChangePopularService(event, params.id)}
            onClick={event => event.stopPropagation()}
          />
        ),
      },
    ],
    [handleChangePopularService]
  );

  function closeConfirmationModal() {
    setIsConfirmModalOpen(false);
  }

  return (
    <>
      <Box my={{ xs: 2, md: 4 }} px={{ xs: 2.5, md: 5 }}>
        <DataGrid
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          sx={{
            bgcolor: COLORS.white3,
            p: 2,
            '& .even': { bgcolor: COLORS.white2 },
          }}
          rowSelection={false}
          onRowClick={params => setSelectedServiceId(params.id)}
          autoHeight
          columns={tableColumns}
          getRowClassName={params =>
            (params.indexRelativeToCurrentPage + 1) % 2 === 0 ? 'even' : 'odd'
          }
          loading={isLoading}
          rows={services}
          slots={{
            toolbar: GridToolbar,
            baseButton: TableActionButton,
            baseTextField: TableSearch,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
        />
      </Box>

      <ServiceModal
        serviceId={selectedServiceId}
        dialogProps={{
          open: Boolean(selectedServiceId),
          onClose: handleServiceModalClose,
        }}
      />

      <ConfirmationModal
        open={isConfirmModalOpen}
        onClose={closeConfirmationModal}
        onClickConfirm={togglePopularService}
        content={{
          title: 'Toggle service popularity',
          description: `Are you sure want to make this service as ${
            pendingServiceUpdateData.isPopular ? 'popular' : 'unpopular'
          }?`,
        }}
      />
    </>
  );
}

const TableActionButton = React.forwardRef(({ children, ...props }, ref) => {
  return (
    <Button disableElevation color='blue' {...props} ref={ref}>
      {children}
    </Button>
  );
});

const TableSearch = props => (
  <TextField
    {...props}
    placeholder='Search....'
    type='search'
    variant='outlined'
    InputProps={{
      sx: { borderRadius: 99 },
      endAdornment: (
        <InputAdornment position='end'>
          <SearchIcon />
        </InputAdornment>
      ),
    }}
  />
);

function parseService(service) {
  const professional = service.Professional ?? {};
  const professionalName =
    [professional.firstName ?? professional.first_name, professional.lastName ?? professional.last_name]
      .filter(Boolean)
      .join(' ') || 'Not available';
  return {
    id: service.id,
    // Show the human-readable professional id (PRO-xxx), never the raw UUID.
    professionalId: professional.professional_id ?? professional.professionalId ?? '',
    profName: professionalName,
    serviceID: service.service_id,
    name: toCapitalize(service.name),
    isPopularService: service.is_popular,
  };
}

export default Services;
